import { useState, type ReactNode } from "react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { AlertCircle, FileText, Play, Plus, RefreshCw, RotateCcw } from "lucide-react";

import { TopicCard } from "@/components/TopicCard";
import { TopicDetailDialog } from "@/components/TopicDetailDialog";
import { NewTopicDialog } from "@/components/NewTopicDialog";
import { MeetingCard } from "@/components/MeetingCard";
import { NewMeetingDialog } from "@/components/NewMeetingDialog";
import { MeetingDetailDialog } from "@/components/MeetingDetailDialog";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader } from "@/components/ui/card";
import { useTopics } from "@/hooks/useTopics";
import { useMeetings } from "@/hooks/useMeetings";
import { DEPARTMENTS } from "@/lib/departments";
import { startOccurrence, type Meeting, type Topic, type TopicStatus } from "@/lib/api";
import { statusMeta } from "@/lib/topic-status";
import { cn } from "@/lib/utils";

/**
 * Step 2 — the dashboard now renders real topics from `GET /api/topics`, bucketed into the
 * three lifecycle columns by status. Loading shows skeletons; each empty column shows an
 * intentional empty state; a failed fetch shows an error with retry. Coverage, AI summaries
 * and "AI suggests…" arrive in Step 3; the per-stage actions are wired up in Step 7.
 */
type StageDef = {
  key: string;
  label: string;
  /** Tailwind bg-* class for the status dot in the column header. */
  accent: string;
  /** Pulse the dot when this stage has topics (the column "comes alive"). */
  live?: boolean;
  /** Topic statuses that belong in this column. */
  statuses: TopicStatus[];
  empty: string;
  action: (ctx: ActionContext) => ReactNode;
};

/** Handlers the stage actions can trigger for a given card's topic. */
type ActionContext = {
  /** Open the live meeting room for this topic. */
  enterRoom: () => void;
  /** Open the topic detail dialog (full summary). */
  openDetail: () => void;
};

const STAGES: StageDef[] = [
  {
    key: "active",
    label: "Active now",
    accent: "bg-emerald-500",
    live: true,
    statuses: ["active"],
    empty: "No live discussions right now.",
    action: ({ enterRoom }) => (
      <Button size="sm" onClick={enterRoom}>
        Join discussion
      </Button>
    ),
  },
  {
    key: "next",
    label: "Up next",
    accent: "bg-amber-500",
    statuses: ["registered"],
    empty: "Nothing queued up yet.",
    action: ({ enterRoom }) => (
      <Button size="sm" onClick={enterRoom}>
        <Play />
        Start now
      </Button>
    ),
  },
  {
    key: "past",
    label: "Past",
    accent: "bg-slate-400",
    statuses: ["resolved", "closed"],
    empty: "No past topics yet.",
    action: ({ enterRoom, openDetail }) => (
      <div className="flex flex-wrap gap-2">
        <Button size="sm" variant="outline" onClick={enterRoom}>
          <RotateCcw />
          Resume
        </Button>
        <Button size="sm" variant="outline" onClick={openDetail}>
          <FileText />
          Review
        </Button>
      </div>
    ),
  },
];

// Staggered page-load reveal — tasteful, matches the portal's existing entrance motion.
const container = {
  hidden: {},
  show: { transition: { staggerChildren: 0.08 } },
};
const column = {
  hidden: { opacity: 0, y: 8 },
  show: { opacity: 1, y: 0, transition: { duration: 0.35, ease: "easeOut" } },
};

export default function Dashboard() {
  const { topics, loading, error, reload } = useTopics();
  const { meetings, loading: meetingsLoading, reload: reloadMeetings } = useMeetings();
  const [selected, setSelected] = useState<Topic | null>(null);
  const [newOpen, setNewOpen] = useState(false);
  const [newMeetingOpen, setNewMeetingOpen] = useState(false);
  const [manageMeeting, setManageMeeting] = useState<Meeting | null>(null);
  const [startingId, setStartingId] = useState<string | number | null>(null);
  const [dept, setDept] = useState("all");
  const navigate = useNavigate();

  const hasUnassigned = topics.some((t) => !t.department);
  const filteredTopics =
    dept === "all" ? topics : topics.filter((t) => (t.department || "Unassigned") === dept);

  async function handleStart(meeting: Meeting) {
    setStartingId(meeting.id);
    try {
      const occurrence = await startOccurrence(meeting.id);
      reloadMeetings();
      navigate(`/room/${occurrence.slug}`);
    } catch {
      // The dashboard stays put on failure — nothing to navigate to.
    } finally {
      setStartingId(null);
    }
  }

  return (
    <div className="mx-auto w-full max-w-6xl">
      <div className="mb-8 flex items-start justify-between gap-3">
        <div>
          <h2 className="text-xl font-semibold tracking-tight">Your meetings</h2>
          <p className="mt-1 text-sm text-muted">
            Recurring series — start this week's occurrence or manage the roster and agenda.
          </p>
        </div>
        <Button size="sm" onClick={() => setNewMeetingOpen(true)}>
          <Plus className="size-3.5" />
          New meeting
        </Button>
      </div>

      <div className="mb-10 grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-3">
        {meetingsLoading && meetings.length === 0 ? (
          <div className="col-span-full">
            <SkeletonCard />
          </div>
        ) : meetings.length === 0 ? (
          <div className="col-span-full">
            <EmptyState message="No meetings yet" />
          </div>
        ) : (
          meetings.map((meeting) => (
            <MeetingCard
              key={meeting.id}
              meeting={meeting}
              starting={startingId === meeting.id}
              onStart={() => void handleStart(meeting)}
              onManage={() => setManageMeeting(meeting)}
            />
          ))
        )}
      </div>

      <div className="mb-8 flex items-start justify-between gap-3">
        <div>
          <h1 className="text-xl font-semibold tracking-tight">Topics</h1>
          <p className="mt-1 text-sm text-muted">
            Meeting topics grouped by where they are in their lifecycle.
          </p>
        </div>
        <div className="flex items-center gap-2">
          <Button size="sm" onClick={() => setNewOpen(true)}>
            <Plus className="size-3.5" />
            New topic
          </Button>
          <Button
            variant="ghost"
            size="icon"
            onClick={reload}
            disabled={loading}
            aria-label="Refresh topics"
          >
            <RefreshCw className={cn(loading && "animate-spin")} />
          </Button>
        </div>
      </div>

      <div className="mb-6 flex items-center gap-2">
        <label htmlFor="dept-filter" className="text-xs text-muted">
          Department
        </label>
        <select
          id="dept-filter"
          value={dept}
          onChange={(e) => setDept(e.target.value)}
          className="rounded-md border border-border bg-bg px-2.5 py-1 text-sm outline-none focus:ring-2 focus:ring-brand-600"
        >
          <option value="all">All departments</option>
          {DEPARTMENTS.map((d) => (
            <option key={d} value={d}>
              {d}
            </option>
          ))}
          {hasUnassigned ? <option value="Unassigned">Unassigned</option> : null}
        </select>
      </div>

      {error ? (
        <ErrorState message={error} onRetry={reload} />
      ) : (
        <motion.div
          variants={container}
          initial="hidden"
          animate="show"
          className="grid grid-cols-1 gap-6 md:grid-cols-3"
        >
          {STAGES.map((stage) => {
            const bucket = filteredTopics
              .filter((t) => stage.statuses.includes(t.status))
              .sort((a, b) => a.position - b.position);
            const live = Boolean(stage.live) && bucket.length > 0;

            return (
              <motion.section key={stage.key} variants={column}>
                <div className="mb-3 flex items-center gap-2">
                  <span className="relative flex h-2 w-2">
                    {live ? (
                      <span
                        className={cn(
                          "absolute inline-flex h-full w-full animate-ping rounded-full opacity-75",
                          stage.accent,
                        )}
                      />
                    ) : null}
                    <span
                      className={cn(
                        "relative inline-flex h-2 w-2 rounded-full",
                        stage.accent,
                      )}
                    />
                  </span>
                  <h2 className="text-sm font-semibold tracking-tight">
                    {stage.label}
                  </h2>
                  {!loading ? (
                    <Badge variant="secondary" className="ml-auto tabular-nums">
                      {bucket.length}
                    </Badge>
                  ) : null}
                </div>

                <div className="space-y-3">
                  {loading ? (
                    <SkeletonCard />
                  ) : bucket.length === 0 ? (
                    <EmptyState message={stage.empty} />
                  ) : (
                    bucket.map((topic) => (
                      <TopicCard
                        key={topic.id}
                        title={topic.title}
                        description={topic.summary_md ?? undefined}
                        provisional={topic.summary_is_provisional}
                        participants={topic.participants}
                        coverage={topic.coverage}
                        templates={topic.templates}
                        department={topic.department}
                        aiSuggestedResolution={topic.ai_suggested_resolution}
                        isPrivate={topic.is_private}
                        badge={statusMeta(topic.status)}
                        action={stage.action({
                          enterRoom: () => navigate(`/room/${topic.slug}`),
                          openDetail: () => setSelected(topic),
                        })}
                        onSelect={() => setSelected(topic)}
                      />
                    ))
                  )}
                </div>
              </motion.section>
            );
          })}
        </motion.div>
      )}

      <TopicDetailDialog
        topic={selected}
        open={selected !== null}
        onOpenChange={(o) => {
          if (!o) setSelected(null);
        }}
      />

      <NewTopicDialog open={newOpen} onOpenChange={setNewOpen} onCreated={reload} />

      <NewMeetingDialog
        open={newMeetingOpen}
        onOpenChange={setNewMeetingOpen}
        onCreated={reloadMeetings}
      />

      {manageMeeting ? (
        <MeetingDetailDialog
          open={manageMeeting !== null}
          onOpenChange={(o) => {
            if (!o) setManageMeeting(null);
          }}
          meetingId={manageMeeting.id}
          onChanged={reloadMeetings}
        />
      ) : null}
    </div>
  );
}

function SkeletonCard() {
  return (
    <Card aria-hidden>
      <CardHeader>
        <div className="flex items-center justify-between gap-3">
          <div className="h-4 w-2/3 animate-pulse rounded bg-elevated" />
          <div className="h-4 w-12 animate-pulse rounded bg-elevated" />
        </div>
        <div className="mt-2 h-8 w-28 animate-pulse rounded bg-elevated" />
      </CardHeader>
    </Card>
  );
}

function EmptyState({ message }: { message: string }) {
  return (
    <div className="rounded-lg border border-dashed border-border p-6 text-center text-sm text-muted">
      {message}
    </div>
  );
}

function ErrorState({
  message,
  onRetry,
}: {
  message: string;
  onRetry: () => void;
}) {
  return (
    <Card className="border-red-500/30">
      <CardHeader>
        <div className="flex items-center gap-2 text-sm font-semibold">
          <AlertCircle className="size-4 text-red-500" />
          Couldn’t load topics
        </div>
        <p className="text-sm text-muted">{message}</p>
      </CardHeader>
      <CardContent>
        <Button size="sm" variant="outline" onClick={onRetry}>
          <RefreshCw />
          Try again
        </Button>
      </CardContent>
    </Card>
  );
}
